/* AMANA — Cadeaux automatiques (dustbag + magazine), module unifié.
 * Remplace deux logiques concurrentes : le bloc inline `dbSync` (dustbag) du theme.liquid
 * et l'ancien `auto-gift.js` (magazine 500 € sur sous-total). Un seul garde.
 *
 * Règles (validées 2026-06-07) :
 *   • Dustbag (49410836627796)  : ajouté si AU MOINS un article payant a final_line_price >= 50,00 €.
 *   • Magazine (55675544142164) : ajouté si le SOUS-TOTAL hors cadeaux >= 500,00 €,
 *     calculé AVANT code réduction (original_line_price = prix de ligne avant remise).
 *   • Exclusion B2B : aucun cadeau pour les clients tagués b2b / wholesale / reseller.
 * Les deux cadeaux sont indépendants (un sac à 350 € déclenche les deux). Les produits
 * d'entretien (tous < 50 €) et les cadeaux (0 €) sont exclus de facto par les seuils.
 *
 * Devise (2026-09-29) : /cart.js et le panier Liquid donnent les prix dans la devise du client.
 * Les seuils sont en euros : ils sont convertis avec Shopify.currency.rate. Avant, 500 SEK
 * (≈ 45 €) suffisait pour le magazine (commandes A5494 en SEK, A5725 en PLN).
 *
 * API partagée avec le tiroir panier (assets/qikify-cart.js) : window.AmanaGifts.
 *   • plan(cart)     → { add: [lignes à ajouter], updates: { clé: quantité } } ; fonction pure.
 *   • fetch          → fetch d'origine (non intercepté) : les mutations du tiroir ne relancent pas ce module.
 *   • settled(cart)  → le tiroir a déjà appliqué le plan ; cart = JSON du panier s'il est connu.
 * Le tiroir calcule le plan AVANT d'envoyer sa mutation et l'applique dans la même requête
 * (ou juste après un ajout) : un seul rendu, cadeaux compris, au lieu de deux.
 *
 * Déclencheurs (2026-09-24) : plus de poll /cart.js toutes les 3 s. Synchro au chargement, après
 * chaque mutation panier interceptée (fetch + XMLHttpRequest, apps tierces), au retour bfcache
 * (pageshow) et quand l'onglet redevient visible (autre onglet / app Shop). MAX_WRITE_STREAK borne
 * les écritures successives si le serveur refuse de converger. Chaque mutation est suivie d'une
 * lecture de confirmation (CONFIRM_MS) : vu en preview, un /cart.js lu juste après un retrait
 * renvoyait encore l'ancien panier, et le magazine restait en place.
 */
(function () {
  'use strict';

  var DUSTBAG_VID  = 49410836627796;
  var DUSTBAG_MIN  = 5000;    // 50,00 € — seuil PAR ARTICLE (final_line_price d'une ligne)
  var MAGAZINE_VID = 55675544142164;
  var MAGAZINE_MIN = 50000;   // 500,00 € — seuil sur le SOUS-TOTAL hors cadeaux, AVANT code réduction
  var GIFT_VIDS    = [DUSTBAG_VID, MAGAZINE_VID];
  var B2B_TAGS     = ['b2b', 'wholesale', 'reseller'];

  var DEBOUNCE_MS     = 400;    // après une mutation panier interceptée
  var INIT_DELAY_MS   = 1200;   // panier pré-rempli au chargement
  var CONFIRM_MS      = 2500;   // 2e lecture après une mutation : /cart.js peut renvoyer l'état d'avant
  var MAX_WRITE_STREAK = 3;     // passes d'écriture d'affilée sans mutation externe

  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?/;
  var origFetch = window.fetch;

  var processing = false;
  var pending = false;        // synchro demandée pendant un passage : rejouée à la fin
  var writeStreak = 0;
  var lastSig = '';           // signature du dernier état STABLE traité (anti-travail inutile)

  function isB2B() {
    var tags = window.__amanaCustomerTags || [];
    for (var i = 0; i < B2B_TAGS.length; i++) {
      if (tags.indexOf(B2B_TAGS[i]) !== -1) return true;
    }
    return false;
  }

  // Taux boutique → devise du client (1 en euros). Seuils en euros, prix du panier en devise client.
  function currencyRate() {
    var c = window.Shopify && window.Shopify.currency;
    var r = c && parseFloat(c.rate);
    return r > 0 ? r : 1;
  }

  function isGift(item) { return GIFT_VIDS.indexOf(Number(item.variant_id)) !== -1; }

  function signature(cart) {
    return cart.items.map(function (i) { return i.variant_id + ':' + i.quantity; }).join(',');
  }

  // Plan de réconciliation : ce qu'il faut ajouter / mettre à jour pour que le panier respecte les
  // règles. Pure (aucune requête) : utilisée ici après lecture de /cart.js, et par le tiroir avant
  // sa propre mutation. Attend des lignes au format /cart.js (prix en centimes, devise du client).
  function plan(cart, opts) {
    opts = opts || {};
    var rate = opts.rate || currencyRate();
    var b2b = opts.b2b != null ? opts.b2b : isB2B();
    var dustbagMin = Math.round(DUSTBAG_MIN * rate);
    var magazineMin = Math.round(MAGAZINE_MIN * rate);

    // ⚠️ Un même variant cadeau peut produire PLUSIEURS lignes (Shopify sépare par properties) :
    // ex. dustbag de l'ancien thème (sans _auto_gift) + dustbag du nouveau (avec marqueur) pendant
    // une transition de publication → vu en prod (commande A4968, 2026-06-12). On collecte TOUTES
    // les lignes de chaque cadeau pour pouvoir dédupliquer.
    var dustbagLines = [], magazineLines = [];
    var hasQualifyingItem = false;   // au moins un article >= 50 €
    var subtotalExclGifts = 0;       // somme des lignes hors cadeaux, avant remise
    var updates = {};

    (cart.items || []).forEach(function (it) {
      if (Number(it.variant_id) === DUSTBAG_VID)  { dustbagLines.push(it);  return; }
      if (Number(it.variant_id) === MAGAZINE_VID) { magazineLines.push(it); return; }
      if (it.quantity <= 0) return;
      subtotalExclGifts += it.original_line_price;
      if (it.final_line_price >= dustbagMin) hasQualifyingItem = true;
      // Sac en quantité > 1 → ramener à 1 (pièces uniques ; course add.js en rafale, 2e onglet,
      // app Shop). Heuristique sac = prix UNITAIRE >= 50 € (même seuil que le dustbag).
      if (it.final_price >= dustbagMin && it.quantity > 1) updates[it.key] = 1;
    });

    var wantDustbag  = !b2b && hasQualifyingItem;
    var wantMagazine = !b2b && (subtotalExclGifts >= magazineMin);

    var add = [];
    // Réconcilie un cadeau à EXACTEMENT une ligne qty 1 (si voulu) ou zéro (sinon).
    function reconcileGift(lines, want, vid, marker) {
      if (!want) { lines.forEach(function (l) { updates[l.key] = 0; }); return; }
      if (lines.length === 0) { add.push({ id: vid, quantity: 1, properties: { _auto_gift: marker } }); return; }
      lines.slice(1).forEach(function (l) { updates[l.key] = 0; });
      if (lines[0].quantity > 1) updates[lines[0].key] = 1;
    }
    reconcileGift(dustbagLines, wantDustbag, DUSTBAG_VID, 'dustbag');
    reconcileGift(magazineLines, wantMagazine, MAGAZINE_VID, 'magazine');

    return { add: add, updates: updates, empty: add.length === 0 && Object.keys(updates).length === 0 };
  }

  function getCart() {
    return origFetch('/cart.js', { cache: 'no-store', credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  function postJson(url, body) {
    return origFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    });
  }

  function refreshDrawer() {
    // Le tiroir et la page /cart partagent le même rendu (snippet qsc2-cart-content) : un seul refresh.
    if (window.QscCart && typeof window.QscCart.refresh === 'function') {
      window.QscCart.refresh();
    } else if (typeof window.refreshCart === 'function') {
      window.refreshCart();   // mises en page sans tiroir AMANA (ancien panier Split)
    }
  }

  // Affichage périmé : le panier a changé sans passer par ce module (cadeaux ajoutés par la page
  // qu'on quittait pendant le chargement de celle-ci, autre onglet). Vu le 29/09 : arrivée par lien
  // partagé sur /cart rendue sans le magazine, plan vide ensuite → aucun rafraîchissement.
  function refreshIfStale(sig) {
    var q = window.QscCart;
    var shown = q && typeof q.signature === 'function' ? q.signature() : null;
    if (shown !== null && shown !== sig) refreshDrawer();
  }

  function done() {
    processing = false;
    if (pending) { pending = false; scheduleSync(); }
  }

  function sync() {
    if (processing) { pending = true; return; }
    processing = true;

    getCart().then(function (cart) {
      var sig = signature(cart);
      if (sig === lastSig) { refreshIfStale(sig); done(); return; }   // état inchangé depuis le dernier passage stable

      var p = plan(cart);
      if (p.empty) {
        lastSig = sig;            // rien à faire : état stable mémorisé
        writeStreak = 0;
        refreshIfStale(sig);
        done();
        return;
      }
      if (writeStreak >= MAX_WRITE_STREAK) { done(); return; }   // le serveur ne converge pas : attendre une mutation externe
      writeStreak++;

      // Ajouts en une requête atomique, puis toutes les corrections en une requête (update.js par clé).
      var chain = Promise.resolve();
      if (p.add.length) chain = chain.then(function () { return postJson('/cart/add.js', { items: p.add }); });
      if (Object.keys(p.updates).length) chain = chain.then(function () { return postJson('/cart/update.js', { updates: p.updates }); });

      chain.then(function () {
        lastSig = '';            // le panier a changé : forcer un recompute au prochain passage
        pending = true;          // passage de vérification (les requêtes du module ne sont pas interceptées)
        done();
        refreshDrawer();
      }).catch(function () {
        lastSig = '';
        done();
      });
    }).catch(function () { done(); });
  }

  var debounceTimer = null;
  function scheduleSync() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(sync, DEBOUNCE_MS);
  }

  var confirmTimer = null;
  function scheduleConfirm() {
    clearTimeout(confirmTimer);
    confirmTimer = setTimeout(function () { lastSig = ''; sync(); }, CONFIRM_MS);
  }

  function externalMutation() {
    writeStreak = 0;
    scheduleSync();
    scheduleConfirm();
  }

  // Le tiroir a appliqué le plan lui-même : pas de synchro immédiate, seulement la lecture de
  // confirmation (filet de sécurité : autre onglet, réponse périmée).
  function settled(cart) {
    writeStreak = 0;
    clearTimeout(debounceTimer);
    if (cart && cart.items) lastSig = signature(cart);
    scheduleConfirm();
  }

  window.AmanaGifts = {
    plan: plan,
    isGift: isGift,
    giftVariantIds: GIFT_VIDS.slice(),
    rate: currencyRate,
    fetch: function () { return origFetch.apply(window, arguments); },
    settled: settled,
    sync: externalMutation
  };

  // --- Déclencheur 1 : après chaque mutation panier, via hook fetch (apps tierces, ancien panier). ---
  window.fetch = function () {
    var first = arguments[0];
    var url = (typeof first === 'string') ? first : (first && first.url) || '';
    var isMutation = CART_MUTATION.test(url);
    var p = origFetch.apply(this, arguments);
    if (isMutation) {
      p.then(function (res) { if (res && res.ok) externalMutation(); }, function () {});
    }
    return p;   // promesse originale intacte pour l'appelant
  };

  // --- Déclencheur 2 : mutations en XMLHttpRequest (apps tierces). ---
  if (window.XMLHttpRequest) {
    var xhrOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (CART_MUTATION.test(String(url || ''))) {
        this.addEventListener('loadend', function () {
          if (this.status >= 200 && this.status < 300) externalMutation();
        });
      }
      return xhrOpen.apply(this, arguments);
    };
  }

  // --- Déclencheur 3 : retour bfcache et onglet redevenu visible (panier modifié ailleurs). ---
  window.addEventListener('pageshow', function (e) { if (e.persisted) scheduleSync(); });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') scheduleSync();
  });

  // --- Déclencheur 4 : au chargement (panier déjà rempli). ---
  if (document.readyState === 'complete') {
    setTimeout(sync, INIT_DELAY_MS);
  } else {
    window.addEventListener('load', function () { setTimeout(sync, INIT_DELAY_MS); });
  }
})();
